import * as React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  CreditCard,
  Receipt,
  Settings,
  ArrowLeft,
  LogOut,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { useAuth } from "@/shared/auth/useAuth";
import { useSession } from "@/shared/auth/SessionContext";
import { VeltroBrandLockup } from "@/shared/components/brand/VeltroBrandLockup";
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/shared/components/ui/sidebar";

const ADMIN_NAV = [
  {
    label: "System Overview",
    items: [
      { label: "Dashboard", href: "/admin/dashboard", icon: LayoutDashboard },
    ],
  },
  {
    label: "Tenant Management",
    items: [
      { label: "Tenants", href: "/admin/tenants", icon: Building2 },
      { label: "All Users", href: "/admin/users", icon: Users },
    ],
  },
  {
    label: "Billing & Plans",
    items: [
      { label: "Plans", href: "/admin/plans", icon: CreditCard },
      { label: "Subscriptions", href: "/admin/billing", icon: Receipt },
    ],
  },
  {
    label: "Configuration",
    items: [
      { label: "System Settings", href: "/admin/system-settings", icon: Settings },
    ],
  },
];

export function AdminSidebar({ ...props }: React.ComponentProps<typeof ShadcnSidebar>) {
  const { t } = useTranslation("admin");
  const { t: tc } = useTranslation("common");
  const { user } = useAuth();
  const { logout } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = React.useState(false);

  return (
    <ShadcnSidebar variant="inset" collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <NavLink to="/admin/dashboard">
                <VeltroBrandLockup
                  size="sm"
                  subtitle="Admin"
                  framed={false}
                  className="w-full"
                />
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {ADMIN_NAV.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{t(`nav.groups.${group.label}`, group.label)}</SidebarGroupLabel>
            <SidebarMenu>
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.href || location.pathname.startsWith(item.href + "/");
                return (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton asChild isActive={active} tooltip={t(`nav.${item.label}`, item.label)}>
                      <NavLink to={item.href}>
                        <Icon />
                        <span>{t(`nav.${item.label}`, item.label)}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip={t("nav.backToApp", "Back to App")}>
              <button type="button" onClick={() => navigate("/")}>
                <ArrowLeft />
                <span>{t("nav.backToApp", "Back to App")}</span>
              </button>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {user && (
            <SidebarMenuItem>
              <div className="flex items-center gap-2 rounded-md bg-sidebar-accent/30 px-2 py-1.5">
                <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                  <span className="truncate text-xs text-muted-foreground">{user.email}</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0"
                  onClick={() => setShowLogoutConfirm(true)}
                  aria-label="Sign out"
                >
                  <LogOut className="size-4" />
                </Button>
              </div>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
        <AlertDialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{tc("signOut.confirmTitle")}</AlertDialogTitle>
              <AlertDialogDescription>{tc("signOut.confirmDescription")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{tc("actions.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={logout}>{tc("signOut.confirm")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SidebarFooter>
    </ShadcnSidebar>
  );
}
